/**
 * Browser Compatibility and Cross-Browser Animation Optimization
 * Handles browser-specific animation issues and provides fallbacks
 */

export interface BrowserInfo {
  name: string;
  version: number;
  engine: string;
  supportsWebAnimations: boolean;
  supportsIntersectionObserver: boolean;
  supportsResizeObserver: boolean;
  supportsPassiveEvents: boolean;
  supportsCSS3DTransforms: boolean;
  supportsWillChange: boolean;
  prefersReducedMotion: boolean;
}

export interface AnimationCapabilities {
  maxConcurrentAnimations: number;
  supportsComplexTransforms: boolean;
  supportsBackdropFilter: boolean;
  supportsClipPath: boolean;
  recommendedFrameRate: number;
  shouldUseGPUAcceleration: boolean;
}

class BrowserCompatibilityManager {
  private browserInfo: BrowserInfo;
  private capabilities: AnimationCapabilities;
  private performanceProfile: 'high' | 'medium' | 'low';

  constructor() {
    this.browserInfo = this.detectBrowser();
    this.capabilities = this.assessCapabilities();
    this.performanceProfile = this.determinePerformanceProfile();
    this.applyBrowserSpecificFixes();
  }

  private detectBrowser(): BrowserInfo {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(navigator.vendor);
    const isEdge = /Edg/.test(userAgent);

    let name = 'unknown';
    let version = 0;
    let engine = 'unknown';

    if (isChrome) {
      name = 'chrome';
      version = parseInt(userAgent.match(/Chrome\/(\d+)/)?.[1] || '0');
      engine = 'blink';
    } else if (isFirefox) {
      name = 'firefox';
      version = parseInt(userAgent.match(/Firefox\/(\d+)/)?.[1] || '0');
      engine = 'gecko';
    } else if (isSafari) {
      name = 'safari';
      version = parseInt(userAgent.match(/Version\/(\d+)/)?.[1] || '0');
      engine = 'webkit';
    } else if (isEdge) {
      name = 'edge';
      version = parseInt(userAgent.match(/Edg\/(\d+)/)?.[1] || '0');
      engine = 'blink';
    }

    return {
      name,
      version,
      engine,
      supportsWebAnimations: 'animate' in document.createElement('div'),
      supportsIntersectionObserver: 'IntersectionObserver' in window,
      supportsResizeObserver: 'ResizeObserver' in window,
      supportsPassiveEvents: this.checkPassiveEventSupport(),
      supportsCSS3DTransforms: this.checkCSS3DSupport(),
      supportsWillChange: this.checkWillChangeSupport(),
      prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
    };
  }

  private checkPassiveEventSupport(): boolean {
    let supportsPassive = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get: () => {
          supportsPassive = true;
          return true;
        }
      });
      window.addEventListener('testPassive', () => {}, opts);
      window.removeEventListener('testPassive', () => {}, opts);
    } catch (e) {
      // Passive events not supported
    }
    return supportsPassive;
  }

  private checkCSS3DSupport(): boolean {
    const el = document.createElement('div');
    el.style.transform = 'translate3d(0,0,0)';
    return el.style.transform !== '';
  }

  private checkWillChangeSupport(): boolean {
    const el = document.createElement('div');
    return 'willChange' in el.style;
  }

  private assessCapabilities(): AnimationCapabilities {
    const { name, version, engine } = this.browserInfo;
    
    // Base capabilities
    let capabilities: AnimationCapabilities = {
      maxConcurrentAnimations: 10,
      supportsComplexTransforms: true,
      supportsBackdropFilter: true,
      supportsClipPath: true,
      recommendedFrameRate: 60,
      shouldUseGPUAcceleration: true
    };

    // Browser-specific adjustments
    if (name === 'safari') {
      // Safari has issues with backdrop-filter performance
      capabilities.supportsBackdropFilter = version >= 14;
      capabilities.maxConcurrentAnimations = version >= 15 ? 8 : 6;
      
      // Safari iOS has different performance characteristics
      if (/iPhone|iPad/.test(navigator.userAgent)) {
        capabilities.maxConcurrentAnimations = 4;
        capabilities.recommendedFrameRate = 30;
      }
    }

    if (name === 'firefox') {
      // Firefox has different transform performance
      capabilities.maxConcurrentAnimations = 8;
      capabilities.supportsBackdropFilter = version >= 103;
    }

    if (name === 'edge' && version < 79) {
      // Legacy Edge limitations
      capabilities.maxConcurrentAnimations = 6;
      capabilities.supportsComplexTransforms = false;
    }

    return capabilities;
  }

  private determinePerformanceProfile(): 'high' | 'medium' | 'low' {
    const { name, version } = this.browserInfo;
    
    // Check for mobile devices
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      return 'medium'; // Conservative approach for mobile
    }

    // Desktop browser performance profiles
    if (name === 'chrome' && version >= 90) return 'high';
    if (name === 'firefox' && version >= 90) return 'high';
    if (name === 'safari' && version >= 14) return 'high';
    if (name === 'edge' && version >= 90) return 'high';

    return 'medium';
  }

  private applyBrowserSpecificFixes(): void {
    const { name, version } = this.browserInfo;

    // Safari-specific fixes
    if (name === 'safari') {
      this.applySafariFixes();
    }

    // Firefox-specific fixes
    if (name === 'firefox') {
      this.applyFirefoxFixes();
    }

    // Chrome-specific optimizations
    if (name === 'chrome') {
      this.applyChromeFixes();
    }
  }

  private applySafariFixes(): void {
    // Fix for Safari's transform-style: preserve-3d issues
    const style = document.createElement('style');
    style.textContent = `
      .safari-3d-fix {
        -webkit-transform-style: preserve-3d;
        transform-style: preserve-3d;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }
      
      /* Fix for Safari's backdrop-filter performance */
      .safari-backdrop-fix {
        -webkit-backdrop-filter: blur(10px);
        backdrop-filter: blur(10px);
        will-change: backdrop-filter;
      }
    `;
    document.head.appendChild(style);
  }

  private applyFirefoxFixes(): void {
    // Firefox-specific animation optimizations
    const style = document.createElement('style');
    style.textContent = `
      /* Firefox transform optimization */
      .firefox-transform-fix {
        will-change: transform;
        transform: translateZ(0);
      }
      
      /* Firefox scroll performance */
      .firefox-scroll-fix {
        scroll-behavior: auto; /* Override smooth scrolling in Firefox */
      }
    `;
    document.head.appendChild(style);
  }

  private applyChromeFixes(): void {
    // Chrome-specific optimizations
    const style = document.createElement('style');
    style.textContent = `
      /* Chrome GPU acceleration optimization */
      .chrome-gpu-fix {
        transform: translateZ(0);
        will-change: transform, opacity;
      }
    `;
    document.head.appendChild(style);
  }

  // Public API
  public getBrowserInfo(): BrowserInfo {
    return this.browserInfo;
  }

  public getCapabilities(): AnimationCapabilities {
    return this.capabilities;
  }

  public getPerformanceProfile(): 'high' | 'medium' | 'low' {
    return this.performanceProfile;
  }

  public shouldUseAnimation(complexity: 'low' | 'medium' | 'high'): boolean {
    if (this.browserInfo.prefersReducedMotion) return false;
    
    const profile = this.performanceProfile;
    
    if (profile === 'high') return true;
    if (profile === 'medium') return complexity !== 'high';
    return complexity === 'low';
  }

  public getOptimalAnimationConfig(animationType: string) {
    const { name } = this.browserInfo;
    const { recommendedFrameRate, shouldUseGPUAcceleration } = this.capabilities;

    const baseConfig = {
      duration: 300,
      easing: 'ease-out',
      useGPU: shouldUseGPUAcceleration,
      frameRate: recommendedFrameRate
    };

    // Browser-specific timing adjustments
    if (name === 'safari') {
      baseConfig.duration *= 1.1; // Slightly longer for Safari
      baseConfig.easing = [0.25, 0.46, 0.45, 0.94];
    }

    if (name === 'firefox') {
      baseConfig.duration *= 0.9; // Slightly faster for Firefox
    }

    return baseConfig;
  }

  public addBrowserClass(): void {
    const { name, version, engine } = this.browserInfo;
    const classes = [
      `browser-${name}`,
      `browser-${name}-${Math.floor(version / 10) * 10}`,
      `engine-${engine}`,
      `performance-${this.performanceProfile}`
    ];

    if (this.browserInfo.prefersReducedMotion) {
      classes.push('reduced-motion');
    }

    document.documentElement.classList.add(...classes);
  }
}

// Singleton instance
export const browserCompatibility = new BrowserCompatibilityManager();

// Initialize browser classes on load
if (typeof window !== 'undefined') {
  browserCompatibility.addBrowserClass();
}

// Export utility functions
export const getBrowserInfo = () => browserCompatibility.getBrowserInfo();
export const getAnimationCapabilities = () => browserCompatibility.getCapabilities();
export const shouldUseAnimation = (complexity: 'low' | 'medium' | 'high') => 
  browserCompatibility.shouldUseAnimation(complexity);
export const getOptimalAnimationConfig = (type: string) => 
  browserCompatibility.getOptimalAnimationConfig(type);